import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { api } from '../api/client'
import type { PublicTracking } from '../api/types'
import { Empty, Loading, Panel, StatusChip, dateTime } from '../components/ui'

/**
 * What the reader is really asking, in one line at the top of the page.
 *
 * "in_transit" is an answer to a different question. Someone who followed a
 * link wants to know whether it has arrived, and if not, when it will.
 */
function headline(tracking: PublicTracking): { value: string; label: string } {
  if (tracking.delivered_at) {
    return { value: 'Delivered', label: dateTime(tracking.delivered_at) }
  }

  if (['failed', 'cancelled'].includes(tracking.status)) {
    return {
      value: 'Not delivered',
      label: 'The transporter has been notified and will be in touch',
    }
  }

  if (tracking.eta_at) {
    return { value: 'On the way', label: `Expected ${dateTime(tracking.eta_at)}` }
  }

  if (tracking.promised_at) {
    return { value: 'On the way', label: `Promised by ${dateTime(tracking.promised_at)}` }
  }

  return { value: 'Booked', label: 'Not yet dispatched' }
}

export default function TrackPage() {
  const { token = '' } = useParams()

  const trackingQuery = useQuery({
    queryKey: ['public-tracking', token],
    // Anonymous on purpose: a consignee has no session, and sending a stale
    // token from an unrelated portal login here would only earn a 401.
    queryFn: () => api.anonymous<PublicTracking>(`/tracking/public/${token}`).then((r) => r.data),
    retry: false,
    // Someone watching a delivery leaves the tab open. A minute is often enough
    // to see a stop tick over without asking much of the API.
    refetchInterval: 60_000,
  })

  const tracking = trackingQuery.data

  return (
    <div className="track-wrap">
      <div className="track-card">
        <div className="track-head">
          <div className="row" style={{ justifyContent: 'center', marginBottom: 14 }}>
            <div className="brand-mark">📦</div>
            <div className="brand-name">Fleey</div>
          </div>

          {tracking && (
            <>
              <div className="track-number">{tracking.shipment_number}</div>
              <div className="track-lane">
                {tracking.origin} → {tracking.destination}
              </div>
            </>
          )}
        </div>

        {trackingQuery.isLoading ? (
          <Loading rows={5} />
        ) : trackingQuery.error ? (
          <Panel>
            <Empty
              title="This tracking link is not valid"
              hint="It may have been mistyped, or the consignment it pointed at has been removed. Ask the sender for a fresh link."
            />
          </Panel>
        ) : tracking ? (
          <div className="stack">
            <div className="track-hero">
              <div className="track-hero-value">{headline(tracking).value}</div>
              <div className="track-hero-label">{headline(tracking).label}</div>
            </div>

            <Panel title="Journey">
              {tracking.milestones.length === 0 ? (
                <Empty title="Nothing recorded yet" hint="Updates appear as the consignment moves." />
              ) : (
                <div className="timeline">
                  {tracking.milestones.map((milestone, i) => (
                    <div
                      key={`${milestone.event}-${i}`}
                      className={`tl-item ${milestone.event === 'delivery.failed' ? 'failed' : 'done'}`}
                    >
                      <div className="tl-title">
                        {milestone.description ?? milestone.event.replace(/[._]/g, ' ')}
                      </div>
                      <div className="tl-time">{dateTime(milestone.occurred_at)}</div>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Stops" bodyless>
              <table className="table">
                <thead>
                  <tr>
                    <th className="num">#</th>
                    <th>Where</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {tracking.stops.map((stop) => (
                    <tr key={stop.sequence}>
                      <td data-label="#" className="num">
                        {stop.sequence}
                      </td>
                      <td data-label="Where">{stop.city ?? '—'}</td>
                      <td data-label="Type" style={{ color: 'var(--text-dim)' }}>
                        {stop.type}
                      </td>
                      <td data-label="Status">
                        <StatusChip status={stop.status} />
                      </td>
                      <td data-label="Completed">
                        {stop.completed_at
                          ? dateTime(stop.completed_at)
                          : stop.eta_at
                            ? `eta ${dateTime(stop.eta_at)}`
                            : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>

            {/* No sign-in prompt and no marketing. Whoever opened this link was
                sent it by someone they trust; nudging them to create an account
                is not what they came for. */}
            <div className="page-sub" style={{ textAlign: 'center' }}>
              This page updates on its own. Keep the link to check again later.
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
